import { useCallback, useEffect, useState } from "react";
import { api, apiError, dateFmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, EmptyState, Th, Td } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchSelect } from "@/components/SearchSelect";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, Pencil, Trash2 } from "lucide-react";

export default function Shopify({ embedded = false }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const canSync = can(user, "shopify:sync");
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mmap, setMmap] = useState([]);
  const [unmapped, setUnmapped] = useState(null);
  const [refStock, setRefStock] = useState(null);
  const [refBusy, setRefBusy] = useState(false);
  const [variants, setVariants] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [messeBundles, setMesseBundles] = useState([]);
  const [editM, setEditM] = useState(null);
  const [assign, setAssign] = useState(null);
  const [articles, setArticles] = useState(null);
  const [artBusy, setArtBusy] = useState(false);
  const [onlyUnmapped, setOnlyUnmapped] = useState(true);

  const load = useCallback(() => {
    api.get("/shopify/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/shopify/sync-logs").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/shopify-match-map").then((r) => setMmap(r.data)).catch(() => {});
    api.get("/shopify/unmapped").then((r) => setUnmapped(r.data)).catch(() => setUnmapped(null));
    api.get("/variants").then((r) => setVariants([...r.data].sort((a, b) => (a.product_name || "").localeCompare(b.product_name || "", "de")))).catch(() => {});
    api.get("/bundles").then((r) => setBundles([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")))).catch(() => {});
    api.get("/messe-bundles").then((r) => setMesseBundles([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setBusy(true);
    try { const r = await api.post("/shopify/sync"); toast.success(r.data.message); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const openEdit = (m) => setEditM({ id: m.id, name: m.name,
    _kind: m.messe_bundle_id ? "messe_bundle" : (m.bundle_id ? "bundle" : "variant"),
    _ref: m.messe_bundle_id || m.bundle_id || m.variant_id || "" });
  const optsFor = (kind) => kind === "messe_bundle"
    ? messeBundles.map((b) => ({ value: b.id, label: `${b.name} · ${b.sku}` }))
    : kind === "bundle"
      ? bundles.map((b) => ({ value: b.id, label: `${b.name} · ${b.sku}` }))
      : variants.map((v) => ({ value: v.id, label: v.product_name ? `${v.product_name} · ${v.sku}` : v.sku }));
  const editOpts = editM ? optsFor(editM._kind) : [];
  const mapBody = (kind, ref) => kind === "messe_bundle" ? { messe_bundle_id: ref }
    : kind === "bundle" ? { bundle_id: ref } : { variant_id: ref };
  const saveMap = async () => {
    if (!editM._ref) { toast.error(t("sh.chooseTarget")); return; }
    try {
      await api.put(`/shopify-match-map/${editM.id}`, mapBody(editM._kind, editM._ref));
      toast.success(t("sh.mappingUpdated")); setEditM(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delMap = async (m) => {
    if (!window.confirm(t("sh.mapDelConfirm", { name: m.name }))) return;
    try { await api.delete(`/shopify-match-map/${m.id}`); toast.success(t("common.deleted")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openAssign = (row) => {
    const bySku = row.suggested_sku ? variants.find((v) => v.sku === row.suggested_sku) : null;
    setAssign({ name: row.name, sku: row.sku ?? row.shopify_sku ?? "",
      shopify_variant_id: row.shopify_variant_id || null,
      store_domain: row.store_domain || null,
      variant_title: row.shopify_variant_title || null,
      product_title: row.shopify_product_title || null,
      selected_options: row.shopify_selected_options || [],
      _kind: "variant", _ref: bySku ? bySku.id : "" });
  };
  const saveAssign = async () => {
    if (!assign._ref) { toast.error(t("sh.chooseTarget")); return; }
    try {
      const useVariant = assign.shopify_variant_id && assign.store_domain;
      const r = useVariant
        ? await api.post("/shopify/assign-variant-mapping", {
            store_domain: assign.store_domain, shopify_variant_id: assign.shopify_variant_id,
            variant_title: assign.variant_title, product_title: assign.product_title,
            ...mapBody(assign._kind, assign._ref) })
        : await api.post("/shopify/assign-mapping", {
            name: assign.name, sku: assign.sku || null, ...mapBody(assign._kind, assign._ref) });
      toast.success(t("sh.assignDone", { lines: r.data.lines_updated, orders: r.data.orders_updated }));
      if (r.data.skipped_locked_orders?.length) {
        toast.warning(t("sh.assignLocked", { orders: r.data.skipped_locked_orders.join(", ") }));
      }
      setAssign(null); setArticles(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const assignOpts = assign ? optsFor(assign._kind) : [];

  const last = status?.last_sync;

  return (
    <div>
      {embedded ? (
        <div className="mb-4 flex items-start justify-between gap-4">
          <p className="text-sm text-muted-foreground">{t("sh.subtitle")}</p>
          {canSync && <Button data-testid="shopify-sync-btn" onClick={sync} disabled={busy}><RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> {t("sh.syncNow")}</Button>}
        </div>
      ) : (
        <PageHeader title={t("sh.title")} subtitle={t("sh.subtitle")}
          actions={canSync && <Button data-testid="shopify-sync-btn" onClick={sync} disabled={busy}><RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> {t("sh.syncNow")}</Button>} />
      )}

      <p className="mb-6 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="shopify-map-gate">{t("sh.mapGate")}</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-md border border-border bg-card p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("sh.connection")}</div>
          <div className="mt-2 flex items-center gap-2 text-lg font-medium">
            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-chart-3" /> {status?.mode || "—"}
          </div>
          <p className="mt-1 text-xs text-muted-foreground" data-testid="shopify-env">
            {t("sh.envLabel")}: <span className="font-mono">{status?.shopify_env || "—"}</span>
            {status?.target_class && <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${status.target_class === "test" ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"}`}>{status.target_class}</span>}
          </p>
        </div>
        <div className="rounded-md border border-border bg-card p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("sh.lastSync")}</div>
          <div className="mt-2 text-lg font-medium">{last ? dateFmt(last.created_at) : t("sh.never")}</div>
          {last && <p className="mt-1 text-xs text-muted-foreground">{last.message}</p>}
        </div>
        <div className="rounded-md border border-border bg-card p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("sh.mappedEntities")}</div>
          <div className="mt-2 font-mono text-2xl font-semibold">{status?.mapped_entities ?? 0}</div>
          <p className="mt-1 text-xs text-muted-foreground">{t("sh.mappedHint")}</p>
        </div>
      </div>

      <h3 className="font-heading text-lg font-medium mb-3">{t("sh.logTitle")}</h3>
      {logs.length === 0 ? <EmptyState testId="shopify-empty" icon={RefreshCw} title={t("sh.noSync")} hint={t("sh.noSyncHint")} /> : (
        <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm">
          <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("common.timestamp")}</Th><Th>{t("common.status")}</Th><Th>{t("sh.message")}</Th><Th right>{t("sh.imported")}</Th><Th right>{t("sh.skipped")}</Th></tr></thead>
            <tbody data-testid="shopify-logs">{logs.map((l) => (
              <tr key={l.id} className="hover:bg-muted/40"><Td>{dateFmt(l.created_at)}</Td>
                <Td>{l.status === "success" ? <span className="inline-flex items-center gap-1 text-primary"><CheckCircle2 className="h-4 w-4" /> {t("sh.success")}</span> : <span className="inline-flex items-center gap-1 text-destructive"><AlertTriangle className="h-4 w-4" /> {t("sh.error")}</span>}</Td>
                <Td>{l.message}</Td><Td right mono>{l.imported ?? 0}</Td><Td right mono>{l.skipped ?? 0}</Td></tr>))}
            </tbody></table>
        </div>
      )}

      <h3 className="font-heading text-lg font-medium mb-1">{t("sh.unmappedTitle")}</h3>
      <p className="text-sm text-muted-foreground mb-3">{t("sh.unmappedHint")}</p>
      {!unmapped || unmapped.count === 0 ? (
        <EmptyState testId="shopify-unmapped-empty" icon={CheckCircle2} title={t("sh.unmappedNone")} hint={t("sh.unmappedNoneHint")} />
      ) : (
        <>
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-md bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive" data-testid="shopify-unmapped-count">
            <AlertTriangle className="h-3.5 w-3.5" /> {t("sh.unmappedSummary", { lines: unmapped.count, orders: unmapped.orders_affected })}
            {unmapped.blocked_orders > 0 && <span> · {t("sh.blockedOrders", { count: unmapped.blocked_orders })}</span>}
          </div>
          {unmapped.truncated && <p className="mb-2 text-xs text-muted-foreground" data-testid="shopify-unmapped-truncated">{t("sh.unmappedTruncated")}</p>}
          <div className="overflow-x-auto rounded-md border border-destructive/40 bg-card shadow-sm" data-testid="shopify-unmapped-table">
            <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("sh.colOrder")}</Th><Th>{t("sh.shopifyName")}</Th><Th>SKU</Th><Th right>{t("common.quantity")}</Th><Th>{t("sh.colSuggestion")}</Th><Th right>{t("common.actions")}</Th></tr></thead>
              <tbody>{unmapped.rows.map((r) => (
                <tr key={`${r.order_id}-${r.line_index}`} className="hover:bg-muted/40" data-testid={`shopify-unmapped-row-${r.order_id}-${r.line_index}`}>
                  <Td><Link to={`/auftraege/${r.order_id}`} className="text-primary hover:underline">{r.order_number || "—"}</Link></Td>
                  <Td>{r.name}
                    {r.shopify_variant_title && <span className="ml-1 text-xs text-muted-foreground">· {r.shopify_variant_title}</span>}
                    {Array.isArray(r.shopify_selected_options) && r.shopify_selected_options.length > 0 &&
                      <span className="ml-1 text-[11px] text-muted-foreground">({r.shopify_selected_options.map((o) => `${o.name}: ${o.value}`).join(", ")})</span>}
                    {r.mapping_note === "sku_ambiguous" && <span className="ml-2 rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-destructive">{t("sh.skuAmbiguous")}</span>}
                    {r.conflict && <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700" data-testid={`shopify-conflict-${r.order_id}-${r.line_index}`}>{t("sh.conflict")}</span>}
                  </Td>
                  <Td mono>{r.sku || "—"}</Td>
                  <Td right mono>{r.quantity}</Td>
                  <Td className="text-muted-foreground">{r.suggested_sku ? `${r.suggested_name || ""} · ${r.suggested_sku}` : "—"}</Td>
                  <Td right>{canSync && <Button size="sm" variant="outline" onClick={() => openAssign(r)} data-testid={`shopify-assign-${r.order_id}-${r.line_index}`}>{t("sh.assign")}</Button>}</Td>
                </tr>))}
              </tbody></table>
          </div>
        </>
      )}

      <h3 className="font-heading text-lg font-medium mb-1 mt-8">{t("sh.refStockTitle")}</h3>
      <p className="text-sm text-muted-foreground mb-3">{t("sh.refStockHint")}</p>
      <Button size="sm" variant="outline" disabled={refBusy} data-testid="shopify-ref-stock-load"
        onClick={async () => {
          setRefBusy(true);
          try { const r = await api.get("/shopify/inventory-reference"); setRefStock(r.data); }
          catch (e) { toast.error(apiError(e)); setRefStock(null); }
          finally { setRefBusy(false); }
        }}>
        <RefreshCw className={`h-4 w-4 mr-1.5 ${refBusy ? "animate-spin" : ""}`} /> {t("sh.refStockLoad")}
      </Button>
      {refStock && (
        <div className="mt-3">
          <p className="mb-2 text-xs text-muted-foreground" data-testid="shopify-ref-stock-summary">
            {t("sh.refStockSummary", { mapped: refStock.mapped, total: refStock.total, unmapped: refStock.unmapped })}
          </p>
          <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm" data-testid="shopify-ref-stock-table">
            <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("sh.shopifyName")}</Th><Th>SKU</Th><Th>{t("sh.internalSku")}</Th><Th right>{t("sh.refStockCol")}</Th></tr></thead>
              <tbody>{refStock.rows.map((r, i) => (
                <tr key={`${r.shopify_sku}-${i}`} className="hover:bg-muted/40">
                  <Td>{r.name}</Td><Td mono>{r.shopify_sku || "—"}</Td>
                  <Td mono>{r.internal_sku || "—"}</Td>
                  <Td right mono>{r.mapped ? r.shopify_quantity ?? "—"
                    : <span className="text-xs font-normal text-destructive">{t("sh.noMatch")}</span>}</Td>
                </tr>))}
              </tbody></table>
          </div>
        </div>
      )}

      <h3 className="font-heading text-lg font-medium mb-1 mt-8">{t("sh.articlesTitle")}</h3>
      <p className="text-sm text-muted-foreground mb-3">{t("sh.articlesHint")}</p>
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant="outline" disabled={artBusy} data-testid="shopify-articles-load"
          onClick={async () => {
            setArtBusy(true);
            try { const r = await api.get("/shopify/product-matching"); setArticles(r.data); }
            catch (e) { toast.error(apiError(e)); setArticles(null); }
            finally { setArtBusy(false); }
          }}>
          <RefreshCw className={`h-4 w-4 mr-1.5 ${artBusy ? "animate-spin" : ""}`} /> {t("sh.articlesLoad")}
        </Button>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={onlyUnmapped} onChange={(e) => setOnlyUnmapped(e.target.checked)} data-testid="shopify-articles-only-unmapped" />
          {t("sh.onlyUnmapped")}
        </label>
      </div>
      {articles && (
        <div className="mt-3">
          <p className="mb-2 text-xs text-muted-foreground" data-testid="shopify-articles-summary">
            {t("sh.refStockSummary", { mapped: articles.mapped, total: articles.total, unmapped: articles.unmapped })}
            {articles.truncated && <span className="ml-2 text-destructive" data-testid="shopify-articles-truncated">{t("sh.articlesTruncated")}</span>}
          </p>
          <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm" data-testid="shopify-articles-table">
            <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("sh.shopifyName")}</Th><Th>SKU</Th><Th>{t("sh.mappedTo")}</Th><Th>{t("sh.colSuggestion")}</Th><Th right>{t("sh.refStockCol")}</Th><Th right>{t("common.actions")}</Th></tr></thead>
              <tbody>{articles.rows.filter((r) => (onlyUnmapped ? !r.mapped : true)).map((r, i) => (
                <tr key={`${r.shopify_sku}-${i}`} className="hover:bg-muted/40" data-testid={`shopify-article-row-${i}`}>
                  <Td>{r.name}
                    {r.mapping_note === "sku_ambiguous" && <span className="ml-2 rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-destructive">{t("sh.skuAmbiguous")}</span>}
                  </Td>
                  <Td mono>{r.shopify_sku || "—"}</Td>
                  <Td>{r.mapped ? <span className="font-mono text-primary">{r.internal_sku || "—"}</span>
                    : <span className="text-xs text-destructive">{t("sh.noMatch")}</span>}</Td>
                  <Td className="text-muted-foreground">{r.suggested_sku ? `${r.suggested_name || ""} · ${r.suggested_sku}` : "—"}</Td>
                  <Td right mono>{r.mapped ? r.shopify_quantity ?? "—" : "—"}</Td>
                  <Td right>{!r.mapped && canSync && <Button size="sm" variant="outline" onClick={() => openAssign(r)} data-testid={`shopify-article-assign-${i}`}>{t("sh.assign")}</Button>}</Td>
                </tr>))}
              </tbody></table>
          </div>
        </div>
      )}

      <h3 className="font-heading text-lg font-medium mb-1 mt-8">{t("sh.mapTitle")}</h3>
      <p className="text-sm text-muted-foreground mb-3">{t("sh.mapHint")}</p>
      {mmap.length === 0 ? <EmptyState testId="mmap-empty" icon={CheckCircle2} title={t("sh.mapEmpty")} hint={t("sh.mapEmptyHint")} /> : (
        <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm" data-testid="mmap-table">
          <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("sh.shopifyName")}</Th><Th>{t("sh.mappedTo")}</Th><Th right>{t("sh.hits")}</Th><Th right>{t("common.actions")}</Th></tr></thead>
            <tbody>{mmap.map((m) => (
              <tr key={m.id} className="hover:bg-muted/40">
                <Td>{m.name}</Td>
                <Td><span className="font-mono">{m.target_sku || m.sku || "—"}</span>{m.target_kind === "bundle" && <span className="ml-1 text-xs text-primary">({t("common.bundle")})</span>}{m.target_kind === "messe_bundle" && <span className="ml-1 text-xs text-chart-4">({t("common.messeBundle")})</span>}{!m.target_ok && <span className="ml-2 text-xs text-destructive">{t("sh.targetDeleted")}</span>}</Td>
                <Td right mono>{m.hits ?? 0}</Td>
                <Td right><div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => openEdit(m)} data-testid={`mmap-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => delMap(m)} data-testid={`mmap-del-${m.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div></Td>
              </tr>))}
            </tbody></table>
        </div>
      )}

      <Dialog open={!!assign} onOpenChange={(o) => !o && setAssign(null)}>
        <DialogContent data-testid="shopify-assign-dialog">
          <DialogHeader><DialogTitle>{t("sh.assignTitle")}</DialogTitle>
            <DialogDescription>{t("sh.assignHint")}</DialogDescription></DialogHeader>
          {assign && <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("sh.shopifyName")}: <span className="text-foreground">{assign.name}</span>{assign.sku && <span className="ml-2 font-mono text-xs">{assign.sku}</span>}</p>
            {(assign.variant_title || assign.product_title) && <p className="text-xs text-muted-foreground">
              {assign.product_title}{assign.variant_title ? ` · ${assign.variant_title}` : ""}
              {Array.isArray(assign.selected_options) && assign.selected_options.length > 0 &&
                <span className="ml-1">({assign.selected_options.map((o) => `${o.name}: ${o.value}`).join(", ")})</span>}
              {assign.shopify_variant_id && <span className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-primary" data-testid="assign-variant-id-badge">{t("sh.variantIdMatch")}</span>}
            </p>}
            <div className="flex items-center gap-2">
              <Select value={assign._kind} onValueChange={(v) => setAssign({ ...assign, _kind: v, _ref: "" })}>
                <SelectTrigger className="w-40" data-testid="assign-kind"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="variant">{t("common.product")}</SelectItem><SelectItem value="bundle">{t("common.bundle")}</SelectItem><SelectItem value="messe_bundle">{t("common.messeBundle")}</SelectItem></SelectContent>
              </Select>
              <div className="flex-1 min-w-0"><SearchSelect testId="assign-ref" value={assign._ref} onChange={(v) => setAssign({ ...assign, _ref: v })} placeholder={t("sh.chooseTargetPh")} options={assignOpts} /></div>
            </div>
          </div>}
          <DialogFooter><Button onClick={saveAssign} data-testid="assign-save">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editM} onOpenChange={(o) => !o && setEditM(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("sh.editMapping")}</DialogTitle></DialogHeader>
          {editM && <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("sh.shopifyName")}: <span className="text-foreground">{editM.name}</span></p>
            <div className="flex items-center gap-2">
              <Select value={editM._kind} onValueChange={(v) => setEditM({ ...editM, _kind: v, _ref: "" })}>
                <SelectTrigger className="w-40" data-testid="mmap-kind"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="variant">{t("common.product")}</SelectItem><SelectItem value="bundle">{t("common.bundle")}</SelectItem><SelectItem value="messe_bundle">{t("common.messeBundle")}</SelectItem></SelectContent>
              </Select>
              <div className="flex-1 min-w-0"><SearchSelect testId="mmap-ref" value={editM._ref} onChange={(v) => setEditM({ ...editM, _ref: v })} placeholder={t("sh.chooseTargetPh")} options={editOpts} /></div>
            </div>
          </div>}
          <DialogFooter><Button onClick={saveMap} data-testid="mmap-save">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
